/**
 * FERP Portal - Audit List Page (DRR-074: Tabulator DRY Template)
 *
 * OPTIMIZE Phase: Audit trail and compliance records.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 *
 * Sprint 3a (DRR-125): LineChart of audit eventCount over time (grouped by createdAt day)
 * using real backend data. No hardcoded data — empty state shown when backend returns no records.
 *
 * Additions:
 * - Framework filter: client-side filter by GqlAuditFrameworkType enum value (DRR-055 dumb frontend).
 * - Generate Report: calls generateAuditReport mutation on the selected audit record (row click).
 *   Backend args: id (audit ID), tenantId, input: GenerateReportInput.
 *
 * Immutable audit trail: display-only. No editing of audit records.
 */

import { useMemo, useState, useCallback } from 'react';
import {
  Paper,
  Stack,
  Title,
  Text,
  Center,
  Group,
  Select,
  Button,
  Modal,
  Alert,
  Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconFileReport,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { usePrimitiveList } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { useMutation } from '@apollo/client/react';
import {
  GqlAuditFrameworkType,
  GqlReportFormat,
  type AuditReportQl,
  type GenerateReportInput,
  useGenerateAuditReportMutation,
  useGetAuditTrailQuery,
  useExportAuditTrailQuery,
} from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ============================================================================
// GRAPHQL
// ============================================================================

// ============================================================================
// HELPERS
// ============================================================================

/** Display labels for GqlAuditFrameworkType values */
const FRAMEWORK_LABELS: Record<string, string> = {
  [GqlAuditFrameworkType.FDA]: 'FDA',
  [GqlAuditFrameworkType.GDPR]: 'GDPR',
  [GqlAuditFrameworkType.HIPAA]: 'HIPAA',
  [GqlAuditFrameworkType.PCI_DSS]: 'PCI DSS',
  [GqlAuditFrameworkType.SOX]: 'SOX',
};

const FRAMEWORK_SELECT_DATA = [
  { value: '', label: 'All frameworks' },
  ...Object.entries(FRAMEWORK_LABELS).map(([value, label]) => ({ value, label })),
];

// ============================================================================
// ACTIVITY CHART
// ============================================================================

function AuditActivityChart() {
  const { items, loading } = usePrimitiveList('audit');

  const chartData = useMemo(() => {
    if (!items.length) return [];

    // Group eventCount by ISO date (YYYY-MM-DD) from createdAt
    const byDay: Record<string, number> = {};
    for (const item of items) {
      if (!item.createdAt) continue;
      const day = String(item.createdAt).slice(0, 10); // "YYYY-MM-DD"
      byDay[day] = (byDay[day] ?? 0) + ((item.eventCount as number) ?? 0);
    }

    // Sort chronologically and format date label
    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, events]) => ({
        day: day.slice(5), // "MM-DD" for compact x-axis labels
        events,
      }));
  }, [items]);

  return (
    <Paper p="md" withBorder radius="md">
      <Title order={5} mb="xs">
        Audit Events Over Time
      </Title>
      {!loading && chartData.length === 0 ? (
        <Center h={160}>
          <Text c="dimmed" size="sm">
            No audit data available
          </Text>
        </Center>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
            <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Events']} />
            <Line
              type="monotone"
              dataKey="events"
              stroke="#f06595"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

// ============================================================================
// GENERATE REPORT MODAL
// ============================================================================

interface GenerateReportModalProps {
  opened: boolean;
  onClose: () => void;
  selectedAudit: PrimitiveItem | null;
  tenantId: string;
}

function GenerateReportModal({
  opened,
  onClose,
  selectedAudit,
  tenantId,
}: GenerateReportModalProps) {
  const { user } = useAuth();
  const [reportFormat, setReportFormat] = useState<string>(GqlReportFormat.PDF);
  const [includeEvidence, setIncludeEvidence] = useState(true);
  const [includeValidation, setIncludeValidation] = useState(true);
  const [result, setResult] = useState<AuditReportQl | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [generateReport, { loading }] = useGenerateAuditReportMutation();

  async function handleGenerate() {
    if (!selectedAudit || !tenantId || !user?.id) return;
    setMutationError(null);
    setResult(null);
    try {
      const res = await generateReport({
        variables: {
          id: selectedAudit.id,
          tenantId,
          input: {
            reportFormat: reportFormat as typeof GqlReportFormat[keyof typeof GqlReportFormat],
            includeEvidence,
            includeValidationDetails: includeValidation,
            requestedBy: user.id,
          },
        },
      });
      if (res.data?.generateAuditReport) {
        setResult(res.data.generateAuditReport);
      }
    } catch (err) {
      setMutationError(String(err));
    }
  }

  function handleClose() {
    setResult(null);
    setMutationError(null);
    onClose();
  }

  const formatData = [
    { value: GqlReportFormat.PDF, label: 'PDF' },
    { value: GqlReportFormat.CSV, label: 'CSV' },
    { value: GqlReportFormat.JSON, label: 'JSON' },
    { value: GqlReportFormat.HTML, label: 'HTML' },
  ];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Generate Audit Report"
      size="sm"
    >
      <Stack gap="sm">
        {selectedAudit ? (
          <Text size="sm">
            Generating report for audit:{' '}
            <strong>{selectedAudit.name ?? selectedAudit.auditName ?? selectedAudit.id}</strong>
          </Text>
        ) : (
          <Alert color="orange" icon={<IconAlertCircle size={14} />}>
            Select an audit record from the table first (click a row), then click Generate Report.
          </Alert>
        )}

        <Select
          label="Report format"
          data={formatData}
          value={reportFormat}
          onChange={(v) => setReportFormat(v ?? GqlReportFormat.PDF)}
        />

        {mutationError && (
          <Alert color="red" icon={<IconAlertCircle size={14} />}>
            {mutationError}
          </Alert>
        )}

        {result && (
          <Alert color="green" icon={<IconCheck size={14} />}>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Report generated successfully</Text>
              <Text size="xs" ff="monospace">ID: {result.reportId}</Text>
              <Text size="xs">
                Format: <Badge size="xs">{result.reportFormat}</Badge>{' '}
                &mdash; {(result.sizeBytes / 1024).toFixed(1)} KB
              </Text>
              {result.reportUrl && (
                <Text
                  size="xs"
                  component="a"
                  href={result.reportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  c="blue"
                >
                  Download report
                </Text>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>

      <Group justify="space-between" mt="lg">
        <Button variant="subtle" onClick={handleClose}>
          Close
        </Button>
        <Button
          leftSection={<IconFileReport size={14} />}
          loading={loading}
          disabled={!selectedAudit || !!result}
          onClick={handleGenerate}
        >
          Generate
        </Button>
      </Group>
    </Modal>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function AuditListPage() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [frameworkFilter, setFrameworkFilter] = useState<string>('');
  const [selectedAudit, setSelectedAudit] = useState<PrimitiveItem | null>(null);
  const [reportOpened, { open: openReport, close: closeReport }] = useDisclosure(false);

  // DRR-125: Audit trail queries for compliance reporting
  const { data: auditTrailData } = useGetAuditTrailQuery({
    variables: { filter: { tenantId, limit: 100 } },
    skip: !tenantId,
  });
  const { data: exportData } = useExportAuditTrailQuery({
    variables: { input: { tenantId, format: 'JSON' } },
    skip: true, // Only execute on-demand via refetch
  });

  // Client-side framework filter delegated to TabulatorPrimitiveManager via clientFilter prop.
  // frameworkType IS on AuditQl (backend-returned field). No hardcoded field logic — filter only
  // applied when user chooses a framework from the Select (DRR-055 dumb frontend).
  const clientFilter = useCallback(
    (item: PrimitiveItem): boolean => {
      if (!frameworkFilter) return true;
      return item.frameworkType === frameworkFilter;
    },
    [frameworkFilter],
  );

  function handleSelectAudit(item: PrimitiveItem) {
    setSelectedAudit(item);
  }

  return (
    <Stack gap="md">
      <AuditActivityChart />

      {/* Filter + action bar */}
      <Group justify="space-between">
        <Select
          placeholder="Filter by framework"
          data={FRAMEWORK_SELECT_DATA}
          value={frameworkFilter}
          onChange={(v) => setFrameworkFilter(v ?? '')}
          clearable
          style={{ width: 220 }}
          size="sm"
        />
        <Group gap="xs">
          {selectedAudit && (
            <Text size="xs" c="dimmed">
              Selected: <strong>{selectedAudit.name ?? selectedAudit.auditName ?? selectedAudit.id}</strong>
            </Text>
          )}
          <Button
            size="sm"
            variant="light"
            leftSection={<IconFileReport size={14} />}
            onClick={openReport}
          >
            Generate Report
          </Button>
        </Group>
      </Group>

      <TabulatorPrimitiveManager
        primitiveType="audit"
        clientFilter={frameworkFilter ? clientFilter : undefined}
        onSelect={handleSelectAudit}
      />

      <GenerateReportModal
        opened={reportOpened}
        onClose={closeReport}
        selectedAudit={selectedAudit}
        tenantId={tenantId}
      />
    </Stack>
  );
}

export default AuditListPage;
